import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "./Reveal";
import { useSimplify } from "./simplify-context";

const PAINS = [
  "Too much Excel",
  "Too many emails",
  "Entering the same information twice",
  "Employees chasing information",
  "Lost follow-ups",
  "Slow invoicing",
  "No clear view of the business",
  "Too many different software systems",
];

export default function SlowDownSection() {
  const { setOpen } = useSimplify();
  const [picked, setPicked] = useState([]);

  const toggle = (pain) =>
    setPicked((p) => (p.includes(pain) ? p.filter((x) => x !== pain) : [...p, pain]));

  return (
    <section className="py-20 lg:py-28 bg-white border-y border-slate-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        <Reveal className="text-center mb-10">
          <h2 className="font-display font-semibold tracking-tight leading-[1.12] text-brand-navy text-2xl sm:text-3xl lg:text-4xl mb-4">
            What's slowing you down?
          </h2>
          <p className="text-base text-slate-600">
            Tap everything that sounds familiar. There's no wrong answer.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div data-testid="slowdown-checklist" className="flex flex-wrap justify-center gap-3">
            {PAINS.map((pain, i) => {
              const active = picked.includes(pain);
              return (
                <button
                  key={pain}
                  data-testid={`slowdown-pain-${i}`}
                  onClick={() => toggle(pain)}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-2.5 rounded-full border px-5 py-3 text-sm font-semibold transition-all duration-300 ${
                    active
                      ? "bg-brand-navy text-white border-brand-navy shadow"
                      : "bg-white text-slate-600 border-slate-200 hover:border-brand-teal hover:text-brand-navy"
                  }`}
                >
                  <span
                    className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors duration-300 ${
                      active ? "bg-brand-teal border-brand-teal" : "border-slate-300"
                    }`}
                  >
                    {active && <Check size={10} strokeWidth={4} className="text-white" />}
                  </span>
                  {pain}
                </button>
              );
            })}
          </div>
        </Reveal>

        <AnimatePresence>
          {picked.length > 0 && (
            <motion.div
              data-testid="slowdown-response"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 rounded-2xl bg-brand-tealsoft border border-brand-tealborder px-7 sm:px-9 py-7 text-center"
            >
              <p className="font-display font-medium text-lg sm:text-xl text-brand-navy leading-snug mb-2">
                {picked.length === 1
                  ? "That one is more common than you'd think."
                  : `Those ${picked.length} share one root cause.`}
              </p>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed max-w-xl mx-auto mb-6">
                Information that doesn't move on its own. People end up carrying
                it — in their heads, their inboxes, their spreadsheets. That's
                exactly what we fix.
              </p>
              <button
                data-testid="slowdown-cta"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-2.5 rounded-full bg-brand-navy text-white text-sm font-semibold px-8 py-3.5 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
              >
                We'll help fix it — show me how
                <ArrowRight size={16} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <Reveal delay={0.1}>
          <p
            data-testid="keep-tools-strip"
            className="mt-12 text-center text-sm sm:text-base text-slate-500 leading-relaxed max-w-xl mx-auto"
          >
            Already have software?{" "}
            <span className="font-semibold text-brand-navy">Keep it.</span>{" "}
            We connect what you already use — Excel, QuickBooks, Microsoft 365,
            Google, Zoho, Autodesk, and the rest. You don't need another
            complicated system.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
